import axios from 'axios';
import type { ApiErrorResponse, ProblemDetailResponse } from '@amuster/types';
import { hasConsent } from '@/lib/cookieConsent';
import { dispatchSessionExpired } from '@/lib/sessionExpiredBus';
import { coordinateRefresh } from '@/lib/refreshCoordinator';

// ── CSRF Token Management ────────────────────────────────────────────────────
// In-memory CSRF token storage (never persisted to localStorage)
let csrfToken: string | null = null;

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/**
 * Clear the in-memory CSRF token. Called on sign-out / session-expiry so a stale
 * token from a previous session can never be reused after a fresh login (the
 * source of post-login `403 CSRF token invalid` during bootstrap). The next safe
 * GET / explicit `/auth/csrf-token` fetch re-mints a session-bound token.
 */
export function clearCsrfToken(): void {
  csrfToken = null;
}

// Methods that require CSRF protection
const CSRF_REQUIRED_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// ── Axios Instance ──────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: '/api',
  headers: {
    'X-Use-Cookies': 'true', // Signal to API that we want cookie-based auth
  },
  withCredentials: true, // Send cookies with every request
});

// ── Request Interceptor ─────────────────────────────────────────────────────

/**
 * Read the CSRF token from the amuster_csrf cookie.
 * Fallback for when in-memory token is lost (e.g. Vite HMR, page re-render).
 * The cookie is NOT httpOnly, so JS can read it.
 */
function readCsrfCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)amuster_csrf=([^;]*)/);
  return match?.[1] ?? null;
}

api.interceptors.request.use((requestConfig) => {
  // Set Content-Type to JSON for non-FormData requests.
  // FormData must NOT have a manually-set Content-Type; the browser
  // sets it automatically with the correct multipart boundary.
  if (!(requestConfig.data instanceof FormData)) {
    requestConfig.headers['Content-Type'] ??= 'application/json';
  }

  // Inject CSRF token for state-changing requests
  const method = (requestConfig.method ?? 'GET').toUpperCase();
  if (CSRF_REQUIRED_METHODS.includes(method)) {
    const token = csrfToken ?? readCsrfCookie();
    if (token) {
      requestConfig.headers['X-CSRF-Token'] = token;
    }
  }
  return requestConfig;
});

// ── Response Interceptor ────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

/** Max time (ms) a queued request waits for an in-flight refresh to resolve. */
const REFRESH_QUEUE_TIMEOUT_MS = 10_000;

/**
 * Drain the entire queue — either resolving all waiters (on success)
 * or rejecting them (on failure / timeout). Resets isRefreshing so
 * subsequent 401s start a fresh refresh attempt.
 */
function processQueue(error: unknown): void {
  const queue = failedQueue;
  failedQueue = [];
  isRefreshing = false;
  queue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });
}

/**
 * Current CSRF token: in-memory first, falling back to the JS-readable
 * `amuster_csrf` cookie. Uses `||` so an empty in-memory value still falls back
 * to the cookie. The value is never logged or persisted to localStorage.
 */
function currentCsrfToken(): string | null {
  return csrfToken || readCsrfCookie();
}

/**
 * Issue the silent token refresh. Uses the raw `axios` instance (so it does NOT
 * recurse through this api instance's 401 handler) BUT carries the CSRF header
 * explicitly — the refresh route enforces CSRF for cookie-auth requests, so the
 * header is required. Its previous absence caused a 403 that the interceptor
 * turned into a hard `/login` redirect before the idle warning could ever fire.
 */
function postRefresh(): Promise<unknown> {
  const csrf = currentCsrfToken();
  return axios.post('/api/auth/refresh', {}, {
    withCredentials: true,
    headers: {
      'X-Use-Cookies': 'true',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
  });
}

// ── API Activity Tracking ───────────────────────────────────────────────────
// Callback invoked on every successful API response. Used by useSessionTimeout
// to track when the last real API call happened (for heartbeat suppression).
let onApiActivity: (() => void) | null = null;

export function setApiActivityCallback(cb: (() => void) | null): void {
  onApiActivity = cb;
}

api.interceptors.response.use(
  (response) => {
    // Notify session timeout hook of API activity
    onApiActivity?.();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - attempt cookie-based refresh.
    //
    // `/auth/me` is INTENTIONALLY eligible here (cold-bootstrap session recovery,
    // PR-C2b-B1): when a cold tab loads with an expired access token but a still-
    // valid refresh cookie, the bootstrap `/auth/me` probe must silently refresh +
    // replay instead of dropping the user to /login. The `_retry` flag caps this at
    // a SINGLE refresh, so a genuinely-expired session still settles cleanly to
    // unauthenticated (no loop), and the failure path below dispatches the central,
    // dirty-aware session-expired signal rather than a hard redirect. The remaining
    // auth routes stay excluded: `/auth/refresh` (no refresh-on-refresh),
    // `/auth/csrf-token` (no session needed), `/auth/login` (bad credentials must
    // not trigger a refresh), and the MFA setup/verify steps (no full session yet).
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/csrf-token') &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/mfa/setup') &&
      !originalRequest.url?.includes('/auth/mfa/verify-setup')
    ) {
      if (isRefreshing) {
        // Queue this request behind the in-flight refresh, with a timeout
        // to prevent indefinite hangs if the refresh request stalls.
        return new Promise((resolve, reject) => {
          const entry = { resolve, reject };
          const timer = setTimeout(() => {
            // The refresh is stuck — drain the entire queue so every
            // waiter fails cleanly and isRefreshing is reset.
            processQueue(new Error('Session refresh timed out'));
          }, REFRESH_QUEUE_TIMEOUT_MS);
          // Wrap so the timer is cancelled when processQueue resolves/rejects us
          entry.resolve = (v: unknown) => { clearTimeout(timer); resolve(v); };
          entry.reject = (e: unknown) => { clearTimeout(timer); reject(e); };
          failedQueue.push(entry);
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Cookie-based refresh: server reads refresh_token from the HttpOnly
        // cookie. postRefresh() attaches the required CSRF header. Run it under
        // cross-tab single-flight so concurrent tabs don't all rotate at once; if
        // a peer tab already refreshed, this resolves without a second rotation
        // (the shared HttpOnly cookies are already fresh).
        await coordinateRefresh(async () => { await postRefresh(); });

        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        // One-time CSRF recovery: the refresh route returns 403 when the CSRF
        // token is missing/stale. Fetch a fresh token ONCE and retry the
        // refresh ONCE before giving up — this avoids a hard logout when the
        // only problem was a stale CSRF token. Capped at a single retry
        // (linear, no loop); the `/auth/csrf-token` GET is excluded from this
        // 401 handler above, so it cannot recurse into another refresh.
        if (axios.isAxiosError(refreshError) && refreshError.response?.status === 403) {
          try {
            const csrfRes = await api.get<{ success: boolean; data: { csrf_token: string } }>(
              '/auth/csrf-token',
            );
            setCsrfToken(csrfRes.data.data.csrf_token);
            await postRefresh();
            processQueue(null);
            return api(originalRequest);
          } catch (retryError) {
            processQueue(retryError);
            // Hand off to the central, dirty-aware session-expired path instead
            // of a hard `/login` redirect that would silently destroy unsaved
            // work. AuthContext owns the final transition.
            dispatchSessionExpired('refresh_csrf_failed');
            return Promise.reject(retryError);
          }
        }
        processQueue(refreshError);
        // Non-recoverable refresh failure (or a peer tab's refresh failed):
        // signal the central, dirty-aware session-expired path. Low-level
        // transport code must NOT hard-redirect to /login.
        dispatchSessionExpired('refresh_failed');
        return Promise.reject(refreshError);
      }
    }

    // 403 instrumentation — fire analytics event but do NOT redirect.
    // Not all 403s are permission denials (feature flags, CORS, CSRF, etc.).
    // Individual pages/route-guards handle the UX; the AccessDeniedPage is
    // navigated to explicitly, not auto-triggered by every API 403.
    if (
      error.response?.status === 403 &&
      !originalRequest.url?.includes('/auth/')
    ) {
      window.dispatchEvent(
        new CustomEvent('amuster:nav', {
          detail: { type: 'access_denied', route_id: originalRequest.url ?? 'unknown', timestamp: Date.now() },
        }),
      );
    }

    return Promise.reject(error);
  }
);

// ── Tenant Key ───────────────────────────────────────────────────────────────
// Retrieves the current tenant key for multi-tenant OAuth flows.
// Checks localStorage first, then falls back to URL-based extraction.
export function getTenantKey(): string | null {
  // Check localStorage (set during tenant login flow) — gated on functional consent
  if (hasConsent('functional')) {
    const stored = localStorage.getItem('tenant_key');
    if (stored) return stored;
  }

  // Extract from subdomain (e.g., acme.amuster.com → acme)
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'app') {
    return parts[0] ?? null;
  }

  // Extract from URL path (e.g., /t/acme/... → acme)
  const pathMatch = window.location.pathname.match(/^\/t\/([^/]+)/);
  if (pathMatch?.[1]) {
    return pathMatch[1];
  }

  return null;
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as (ApiErrorResponse | ProblemDetailResponse) | undefined;
    // RFC 9457 Problem Details format (detail field)
    if (data && 'detail' in data && typeof data.detail === 'string') {
      return data.detail;
    }
    // Legacy format (error.message field)
    if (data && 'error' in data && (data as ApiErrorResponse).error?.message) {
      return (data as ApiErrorResponse).error.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

export default api;
